import React from 'react';
import { Link } from 'react-router-dom';
import { Radio, ShieldAlert, ExternalLink, Heart } from 'lucide-react';
import { APP_CONFIG } from '../../config/appConfig';

export const Footer: React.FC = () => {
  return (
    <footer className="bg-navy-950 text-slate-300 border-t border-slate-800 text-xs mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Brand Info */}
          <div className="md:col-span-1 space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-blue-600 rounded flex items-center justify-center text-white">
                <Radio className="w-4 h-4" />
              </div>
              <span className="text-base font-bold text-white tracking-tight">
                SignalNear
              </span>
            </div>
            <p className="text-slate-400 text-xs leading-relaxed">
              Automated public disruption radar aggregating active emergency, storm, and severe weather warnings across the United States.
            </p>
            <div className="pt-2 text-[11px] text-slate-500">
              Primary Data Source: US National Weather Service (weather.gov)
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-3 uppercase tracking-wider text-[11px]">
              Radar & Alerts
            </h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="hover:text-white transition-colors">
                  National Radar Overview
                </Link>
              </li>
              <li>
                <Link to="/alerts" className="hover:text-white transition-colors">
                  All Active US Alerts
                </Link>
              </li>
              <li>
                <Link to="/locations" className="hover:text-white transition-colors">
                  All 50 US States Directory
                </Link>
              </li>
              <li>
                <Link to="/hazards/tornado" className="text-red-400 hover:text-red-300 transition-colors">
                  Tornado Warnings Radar
                </Link>
              </li>
              <li>
                <Link to="/hazards/flood" className="hover:text-white transition-colors">
                  Flash Flood Radar
                </Link>
              </li>
              <li>
                <Link to="/hazards/earthquake" className="text-purple-400 hover:text-purple-300 transition-colors">
                  USGS Earthquake Radar
                </Link>
              </li>
              <li>
                <Link to="/history" className="text-amber-400 hover:text-amber-300 transition-colors">
                  24h Cleared Storm Archive
                </Link>
              </li>
              <li>
                <Link to="/saved" className="text-cyan-400 hover:text-cyan-300 transition-colors">
                  Family / Saved Locations
                </Link>
              </li>
              <li>
                <Link to="/embed" className="hover:text-white transition-colors">
                  Free Embed Radar Widget
                </Link>
              </li>
            </ul>
          </div>

          {/* Official Safety Resources */}
          <div>
            <h3 className="text-white font-semibold mb-3 uppercase tracking-wider text-[11px]">
              Official Safety Portals
            </h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="https://www.weather.gov"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 hover:text-white transition-colors"
                >
                  <span>National Weather Service</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                <a
                  href="https://www.ready.gov"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 hover:text-white transition-colors"
                >
                  <span>Ready.gov Disaster Preparedness</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                <a
                  href="https://www.redcross.org/get-help.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 hover:text-white transition-colors"
                >
                  <span>American Red Cross Disaster Help</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                <a
                  href="https://www.fema.gov"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 hover:text-white transition-colors"
                >
                  <span>FEMA Emergency Information</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>

          {/* Legal & Policy */}
          <div>
            <h3 className="text-white font-semibold mb-3 uppercase tracking-wider text-[11px]">
              Governance & Policies
            </h3>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="hover:text-white transition-colors">
                  About & Methodology
                </Link>
              </li>
              <li>
                <Link to="/sources" className="hover:text-white transition-colors">
                  Verified Data Sources
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-white transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="hover:text-white transition-colors">
                  Terms of Service & Disclaimer
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-white transition-colors">
                  Contact & Inquiries
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Public Disclaimer Banner in Footer */}
        <div className="pt-6 border-t border-slate-800 text-[11px] text-slate-400 space-y-2">
          <div className="flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p>
              <strong>NON-GOVERNMENT PUBLIC NOTICE:</strong> SignalNear is an independent public information platform. We are not affiliated with, endorsed by, or operated by the National Weather Service, NOAA, FEMA, or any municipal emergency dispatch agency. In the event of an imminent danger to life or property, consult local authorities or call 911 immediately.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 text-slate-500">
            <div>
              © {new Date().getFullYear()} {APP_CONFIG.name}. Public domain meteorological data courtesy of NOAA / National Weather Service.
            </div>
            <div>
              Map tiles © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className="hover:underline text-slate-400">OpenStreetMap contributors</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

